// frontend/components/sidebar/docPermission/PermSelect.tsx
//
// 권한 설정 모달에서 사용하는 커스텀 드롭다운 셀렉트입니다.
// 버튼 위치를 기준으로 패널을 위/아래 중 화면에 들어가는 방향에 띄웁니다.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

const PERM_COLOR: Record<string, string> = {
  NONE:    "text-gray-400 dark:text-zinc-500",
  READ:    "text-gray-600 dark:text-zinc-300",
  COMMENT: "text-yellow-600 dark:text-yellow-400",
  EDIT:    "text-green-600 dark:text-green-400",
  FULL:    "text-blue-600 dark:text-blue-400",
};

export default function PermSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const panelH = options.length * 34 + 8;
      const top = window.innerHeight - rect.bottom >= panelH
        ? rect.bottom + 4
        : rect.top - panelH - 4;
      setPos({ top, right: window.innerWidth - rect.right });
    }
    setOpen(v => !v);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = options.find(o => o.value === value);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1.5 bg-gray-100 dark:bg-zinc-700 border border-gray-200 dark:border-zinc-600 rounded outline-none cursor-pointer hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors whitespace-nowrap ${PERM_COLOR[value] || "text-gray-700 dark:text-zinc-200"}`}
      >
        {current?.label}
        <ChevronDown size={10} className="text-gray-400 dark:text-zinc-400 shrink-0" />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: pos.top, right: pos.right }}
          className="fixed z-9999 w-28 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 rounded-lg shadow-xl py-1"
        >
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                opt.value === value
                  ? "bg-blue-50 dark:bg-zinc-700 text-blue-600 dark:text-blue-400 font-semibold"
                  : "text-gray-700 dark:text-zinc-200 hover:bg-gray-50 dark:hover:bg-zinc-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
